import { useState } from 'react'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  GripVertical,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  RotateCcw,
  Slack,
  X,
} from 'lucide-react'
import './SlackLayoutEditor.css'

export interface MetricOption {
  id: string
  label: string
  group: string
}

/** What the snapshot SQL returned for one table (from /digest-sql/metrics). */
export interface CatalogEntry {
  id: string
  heading: string
  dim: string
  single: boolean // weekly trend: one metric across the week columns
  defaults: string[]
  available: string[]
}

export interface LayoutRow {
  id: string
  heading: string
  on: boolean
  metrics: string[]
  defaults: string[]
  dim: string // first column, e.g. Brand
}

export type CatalogStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready'; analysisDate: string; stale: boolean }
  | { state: 'error'; message: string }

const WEEKLY = 'brand_weekly_epc'
const WEEK_COLUMNS = ['Week 1', '…', 'Week 6']
const PERIODS = 'Last 7 Days, MTD and YTD'

interface Props {
  performance: LayoutRow // message 1's table: always shown, only its columns change
  rows: LayoutRow[] // message 2's detail tables
  metricOptions: MetricOption[]
  catalog: CatalogEntry[] | null
  status: CatalogStatus
  source: string // e.g. "Performance + Weekly EPC"
  previewing: boolean
  canPreview: boolean
  onPerformanceChange: (row: LayoutRow) => void
  onChange: (rows: LayoutRow[]) => void
  onRefresh: () => void
  onPreview: () => void
}

/** Header text for a metric column, as the digest engine writes it. */
export function columnLabels(metrics: string[], labels: Record<string, string>): string[] {
  return metrics.map((m, i) => {
    if (!m.endsWith('_wow')) return labels[m] || m
    const base = m.slice(0, -4)
    return metrics[i - 1] === base ? 'Δ%' : `${labels[base] || base} Δ%`
  })
}

export default function SlackLayoutEditor({
  performance,
  rows,
  metricOptions,
  catalog,
  status,
  source,
  previewing,
  canPreview,
  onPerformanceChange,
  onChange,
  onRefresh,
  onPreview,
}: Props) {
  const [expanded, setExpanded] = useState(false) // the whole layout panel; closed by default
  const [open, setOpen] = useState<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)

  const labels = Object.fromEntries(metricOptions.map((m) => [m.id, m.label]))
  const order = metricOptions.map((m) => m.id)
  const groups = [...new Set(metricOptions.map((m) => m.group))]
  const shown = rows.filter((r) => r.on)
  const hidden = rows.filter((r) => !r.on)
  const entry = (id: string) => catalog?.find((c) => c.id === id)
  // With no catalog yet (or a failed read) nothing is greyed out.
  const isAvailable = (id: string, m: string) => !catalog || !!entry(id)?.available.includes(m)
  const hasData = (id: string) => !catalog || (entry(id)?.available.length ?? 0) > 0
  const isDefault = (r: LayoutRow) => r.metrics.join() === r.defaults.join()

  const update = (id: string, patch: Partial<LayoutRow>) => {
    if (id === performance.id) return onPerformanceChange({ ...performance, ...patch })
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }

  const move = (from: number, to: number) => {
    if (to < 0 || to >= shown.length) return
    const next = [...shown]
    const [row] = next.splice(from, 1)
    next.splice(to, 0, row)
    onChange([...next, ...hidden])
  }

  const toggleMetric = (row: LayoutRow, m: string) => {
    if (row.id === WEEKLY) return update(row.id, { metrics: [m] })
    if (row.metrics.includes(m)) {
      if (row.metrics.length > 1) update(row.id, { metrics: row.metrics.filter((x) => x !== m) })
      return
    }
    // Columns keep the catalog order (each Δ% right after its metric); a default
    // set that isn't in that order keeps its own order until it's changed.
    update(row.id, {
      metrics: [...row.metrics, m].sort((a, b) => order.indexOf(a) - order.indexOf(b)),
    })
  }

  const summary = (row: LayoutRow) =>
    row.id === WEEKLY
      ? `${labels[row.metrics[0]] || row.metrics[0]} for each of 6 weeks`
      : columnLabels(row.metrics, labels).join(' · ')

  const metricPicker = (row: LayoutRow) => (
    <div className="sl-metrics">
      <div className="sl-metrics-head">
        <span className="st-hint">
          {row.id === WEEKLY
            ? 'Pick the metric shown for each week.'
            : row.id === performance.id
              ? `Pick the columns. Rows are ${PERIODS}; Δ% compares each with the period before.`
              : 'Pick the columns. Δ% compares the last 7 days with the 7 days before.'}
        </span>
        {!isDefault(row) && (
          <button type="button" className="sl-link sl-inline" onClick={() => update(row.id, { metrics: row.defaults })}>
            <RotateCcw size={12} aria-hidden /> Reset to default
          </button>
        )}
      </div>
      {groups.map((g) => {
        const ids = metricOptions
          .filter((m) => m.group === g)
          .map((m) => m.id)
          .filter((m) => row.id !== WEEKLY || ['epc', 'revenue', 'clickouts'].includes(m))
          .filter((m) => row.id !== performance.id || m !== 'share')
        if (!ids.length) return null
        return (
          <div key={g} className="sl-metric-group">
            <span className="sl-group-label">{g}</span>
            <div className="sl-chips">
              {ids.map((m) => {
                const on = row.metrics.includes(m)
                const avail = isAvailable(row.id, m)
                return (
                  <button
                    key={m}
                    type="button"
                    role={row.id === WEEKLY ? 'radio' : 'checkbox'}
                    aria-checked={on}
                    className={`sl-chip${on ? ' on' : ''}${!avail ? ' na' : ''}`}
                    disabled={(!avail && !on) || (on && row.metrics.length === 1 && row.id !== WEEKLY)}
                    title={
                      !avail
                        ? 'The snapshot SQL doesn’t return the columns for this metric in this table'
                        : m.startsWith('gross_profit')
                          ? 'Revenue minus spend'
                          : undefined
                    }
                    onClick={() => toggleMetric(row, m)}
                  >
                    {labels[m] || m}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )

  const cardMain = (row: LayoutRow) => {
    const expanded = open === row.id
    const missing = row.metrics.filter((m) => !isAvailable(row.id, m))
    return (
      <>
        <button
          type="button"
          className="sl-card-main"
          aria-expanded={expanded}
          onClick={() => setOpen(expanded ? null : row.id)}
        >
          <span className="sl-card-title">
            {row.heading}
            {!hasData(row.id) && <span className="st-col-badge no-access">No data in this SQL</span>}
            {hasData(row.id) && missing.length > 0 && (
              <span className="st-col-badge no-access">
                {missing.length} column{missing.length > 1 ? 's' : ''} not in SQL
              </span>
            )}
          </span>
          <span className="sl-card-cols">{summary(row)}</span>
        </button>
        <button
          type="button"
          className="sl-icon-btn"
          aria-label={`Edit columns for ${row.heading}`}
          onClick={() => setOpen(expanded ? null : row.id)}
        >
          {expanded ? <ChevronUp size={15} aria-hidden /> : <ChevronDown size={15} aria-hidden />}
        </button>
      </>
    )
  }

  const customised = [performance, ...shown].filter((r) => !isDefault(r)).length
  const toggle = () => {
    // The metrics are read from the SQL (a BigQuery run) the first time the panel opens.
    if (!expanded && status.state === 'idle') onRefresh()
    setExpanded(!expanded)
  }

  return (
    <div className={`sl-editor${expanded ? ' open' : ''}`}>
      <button type="button" className="sl-toggle" aria-expanded={expanded} onClick={toggle}>
        <span className="sl-toggle-icon" aria-hidden>
          <Slack size={16} />
        </span>
        <span className="sl-toggle-text">
          <span className="sl-title">Slack digest layout</span>
          <span className="sl-toggle-sub">
            Performance + {shown.length} detail table
            {shown.length === 1 ? '' : 's'}
            {customised > 0 ? ` · ${customised} with custom columns` : ''} · from {source}
          </span>
        </span>
        <ChevronRight size={18} className="sl-toggle-chevron" aria-hidden />
      </button>
      {expanded && (
        <div className="sl-body">
          <div className="sl-head">
            <div className="st-hint">Choose the columns of each table and the order of the detail tables.</div>
            {/* Fixed-size slots: labels never change, so nothing shifts while loading. */}
            <div className="sl-status">
              <span
                className={`st-hint sl-status-text${status.state === 'ready' && status.stale ? ' sl-warn' : ''}`}
                aria-live="polite"
              >
                {status.state === 'loading' && 'Reading metrics…'}
                {status.state === 'ready' && !status.stale && `Metrics as of ${status.analysisDate}`}
                {status.state === 'ready' && status.stale && (
                  <>
                    <AlertTriangle size={12} aria-hidden /> SQL changed, refresh
                  </>
                )}
              </span>
              <button
                type="button"
                className="sl-icon-btn sl-refresh"
                onClick={onRefresh}
                disabled={status.state === 'loading'}
                title="Read the metrics again from the snapshot SQL"
              >
                <RefreshCw size={13} className={status.state === 'loading' ? 'st-spin' : ''} aria-hidden /> Refresh
              </button>
              <span className="sl-actions">
                <button
                  type="button"
                  className="sl-preview-btn"
                  disabled={!canPreview || previewing}
                  onClick={onPreview}
                  title={
                    previewing ? 'Building the preview from live data…' : 'Build both Slack messages from live data'
                  }
                >
                  {previewing ? <Loader2 size={14} className="st-spin" aria-hidden /> : <Eye size={14} aria-hidden />}
                  Slack Preview
                </button>
              </span>
            </div>
          </div>
          {status.state === 'error' && (
            <div className="st-error" style={{ display: 'block', marginBottom: 10 }}>
              {status.message} Every metric is shown until the SQL can be read.
            </div>
          )}

          <div className="sl-grid">
            <div>
              <div className="sl-msg-label">Message 1 · Overview</div>
              <div className="sl-card pinned">
                <div className="sl-card-row">
                  <span className="sl-grip" title="Always first in message 1" aria-hidden>
                    <Lock size={13} />
                  </span>
                  {cardMain(performance)}
                </div>
                {open === performance.id && metricPicker(performance)}
              </div>
              <div className="st-hint sl-msg-note">Followed by the AI summary, key insights and actions.</div>

              <div className="sl-msg-label">Message 2 · Detail tables</div>
              <ol className="sl-list" aria-label="Detail tables in Slack message 2">
                {shown.map((row, i) => (
                  <li
                    key={row.id}
                    className={`sl-card${dragId === row.id ? ' dragging' : ''}${overId === row.id && dragId !== row.id ? ' over' : ''}`}
                    draggable
                    onDragStart={(e) => {
                      setDragId(row.id)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      setOverId(row.id)
                    }}
                    onDragLeave={() => setOverId((o) => (o === row.id ? null : o))}
                    onDrop={(e) => {
                      e.preventDefault()
                      const from = shown.findIndex((r) => r.id === dragId)
                      if (from >= 0) move(from, i)
                      setDragId(null)
                      setOverId(null)
                    }}
                    onDragEnd={() => {
                      setDragId(null)
                      setOverId(null)
                    }}
                  >
                    <div className="sl-card-row">
                      <span className="sl-grip" title="Drag to reorder" aria-hidden>
                        <GripVertical size={15} />
                      </span>
                      <span className="sl-num">{i + 1}</span>
                      {cardMain(row)}
                      <span className="sl-move">
                        <button
                          type="button"
                          className="sl-icon-btn"
                          aria-label={`Move ${row.heading} up`}
                          disabled={i === 0}
                          onClick={() => move(i, i - 1)}
                        >
                          <ChevronUp size={13} aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="sl-icon-btn"
                          aria-label={`Move ${row.heading} down`}
                          disabled={i === shown.length - 1}
                          onClick={() => move(i, i + 1)}
                        >
                          <ChevronDown size={13} aria-hidden />
                        </button>
                      </span>
                      <button
                        type="button"
                        className="sl-icon-btn"
                        aria-label={`Remove ${row.heading} from the message`}
                        title={shown.length === 1 ? 'The message needs at least one table' : 'Remove from message'}
                        disabled={shown.length === 1}
                        onClick={() => update(row.id, { on: false })}
                      >
                        <X size={15} aria-hidden />
                      </button>
                    </div>
                    {open === row.id && metricPicker(row)}
                  </li>
                ))}
              </ol>

              {hidden.length > 0 && (
                <div className="sl-hidden">
                  <div className="sl-group-label">Not in the message</div>
                  <div className="sl-hidden-list">
                    {hidden.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        className="sl-add"
                        disabled={!hasData(row.id)}
                        title={
                          hasData(row.id) ? `Add ${row.heading}` : 'The snapshot SQL returns no rows for this table'
                        }
                        onClick={() =>
                          onChange([...shown, { ...row, on: true }, ...hidden.filter((r) => r.id !== row.id)])
                        }
                      >
                        <Plus size={13} aria-hidden /> {row.heading}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="sl-mock" aria-label="Message outline">
              <div className="sl-mock-label">Message 1</div>
              <div className="sl-mock-sec">
                <div className="sl-mock-heading">{performance.heading}</div>
                <div className="sl-mock-cols">
                  {[performance.dim, ...columnLabels(performance.metrics, labels)].map((c, j) => (
                    <span key={j} className={j === 0 ? 'dim' : ''}>
                      {c}
                    </span>
                  ))}
                </div>
                <div className="sl-mock-text">Summary · Insights · Actions</div>
              </div>
              <div className="sl-mock-label" style={{ marginTop: 14 }}>
                Message 2
              </div>
              {shown.map((row) => (
                <div key={row.id} className="sl-mock-sec">
                  <div className="sl-mock-heading">
                    ▸ {row.id === WEEKLY ? `6-Week ${labels[row.metrics[0]] || ''} Trend` : row.heading}
                  </div>
                  <div className="sl-mock-cols">
                    {[row.dim, ...(row.id === WEEKLY ? WEEK_COLUMNS : columnLabels(row.metrics, labels))].map(
                      (c, j) => (
                        <span key={j} className={j === 0 ? 'dim' : ''}>
                          {c}
                        </span>
                      )
                    )}
                  </div>
                </div>
              ))}
            </aside>
          </div>
        </div>
      )}
    </div>
  )
}
